/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { v4 as uuidv4 } from 'uuid';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import {
  Button,
  Box,
  Grid,
  Stack,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useHistory, useParams } from 'react-router-dom';
import { User } from '@alumni-ventures-group/av-okta-auth';
import {
  Container, Row, Card, CardBody, Col,
} from 'reactstrap';
import PropTypes from 'prop-types';
import WarnIcon from '@mui/icons-material/WarningAmber';

import useDealDetail from '../shared/hooks/usePendingInvestmentDetail';
import createDealsTableDefinition from './DealDetailTableDefinition';
import createDealTotalsTableDefinition from './DealTotalsTableDefinition';
import AvDataGrid from '../shared/components/AvDataGrid';
import ReadOnlyTextField from '../shared/components/ReadOnlyTextField';
import exportGridValues from '../shared/components/AvDataGrid/exportGridValues';
import HorizRecLoader from '../shared/components/Loader/HorizRecLoader';
import getNumberValue from '../shared/utils/getNumberValue';
import formatCurrencyAsUsd from '../shared/utils/formatCurrencyAsUsd';
import roundToPrecision from '../shared/utils/roundToPrecision';
import { updatePendingInvestment, submitPendingInvestment } from '../shared/services/apiGateway';
import generateRequest from '../shared/services/generateRequest';
import ConfirmationModal from '../shared/components/ConfirmationModal';
import CustomTextField from './PostWireSignOffNetSuiteCustomTextField';
import DocumentViewer from '../shared/components/DocumentViewer';
import {
  createFundConfigurationError,
  assignLocalEditValues,
  securityTypes,
  prepareFundAllocationsForGrid,
  checkPositiveWholeNumber,
  checkIfInterestLoanSecurity,
  checkIfNoInterestLoanSecurity,
  checkIfStockSecurity,
  checkIfTokenSecurity,
  checkIfNullOrUndefined,
  checkNaturalNumber,
  checkValidPercentageBounds,
  undefinedSecurityTypeLabel,
} from './helpers';

const maxAllowableSharePriceXNumberUnitsDiscrepancy = 0.03;
const maxAllowableWireAmountVsTotalInvestmentAmountDiscrepancy = 0.03;
const maxAllowableWireAmountVsTotalAllocationAcrossSecuritiesDiscrepancy = 0.03;

// A compact input style with max width 250px.
const compactInputStyle = {
  width: '100%',
  minWidth: '225px',
  maxWidth: '225px',
  marginBottom: '10px',
};

function StyledActionButton({ children, ...props }) {
  return (
    <LoadingButton variant="outlined" color="secondary" sx={{ m: 1 }} {...props}>
      {children}
    </LoadingButton>
  );
}

function createAggregatedFunds(formattedFundAllocations) {
  const aggregatedFunds = {};
  formattedFundAllocations.forEach((fund) => {
    const key = `${fund.name}`;
    if (!aggregatedFunds[key]) {
      aggregatedFunds[key] = { ...fund };
    } else {
      aggregatedFunds[key].investmentAmount += getNumberValue(fund.investmentAmount);
      aggregatedFunds[key].aAllocation += getNumberValue(fund.aAllocation);
      aggregatedFunds[key].qAllocation += getNumberValue(fund.qAllocation);
    }
  });
  return Object.values(aggregatedFunds);
}

function localToggleVisibilityOfFieldsBySecurityType(types, selectedSecurityType) {
  const filteredSecurityType = types.find((securityType) => securityType.id === selectedSecurityType);
  if (!filteredSecurityType || filteredSecurityType.name === undefinedSecurityTypeLabel) {
    return { showInterestRate: false, showSharePrice: false, showNumberOfUnits: false };
  }
  if (checkIfStockSecurity(filteredSecurityType.name) || checkIfTokenSecurity(filteredSecurityType.name)) {
    return { showInterestRate: false, showSharePrice: true, showNumberOfUnits: true };
  }
  if (checkIfNoInterestLoanSecurity(filteredSecurityType.name)) {
    return { showInterestRate: false, showSharePrice: false, showNumberOfUnits: false };
  }
  if (checkIfInterestLoanSecurity(filteredSecurityType.name)) {
    return { showInterestRate: true, showSharePrice: false, showNumberOfUnits: false };
  }
  return { showInterestRate: false, showSharePrice: false, showNumberOfUnits: false };
}

function createProratedFunds(originalFunds, securityAllocations) {
  const newFunds = [];
  const aggregatedFunds = createAggregatedFunds(originalFunds);
  securityAllocations.forEach((allocation) => {
    aggregatedFunds.forEach((fund) => {
      const proratedFund = { ...fund };
      const originalAmount = getNumberValue(fund.investmentAmount);
      proratedFund.investmentAmount = originalAmount * (allocation.allocation / 100);
      proratedFund.securityTypeId = allocation.securityType;
      proratedFund.securityTypeName = securityTypes.find((s) => s.id === allocation.securityType)?.name || '';
      newFunds.push(proratedFund);
    });
  });
  // if isMultiSecurityDeal, round to 2 decimal places. techinically this should not be needed
  // but can help with rounding errors while retaining single security deal functionality
  // if (securityAllocations.length > 1) {
  //   proratedFund.investmentAmount = roundToPrecision(proratedFund.investmentAmount, 2);
  // }
  return newFunds;
}

function SecurityAllocationManager({
  securityAllocations,
  setSecurityAllocations,
  securityTypes,
  isEditable,
  aggregatedInvestments,
  wireAmount,
}) {
  // helper to update a single field
  const updateField = (id, field, value) => {
    setSecurityAllocations((current) => current.map((a) => (a.id === id
      ? { ...a, [field]: value }
      : a)));
  };

  const handleAllocationAmountChange = (id, rawValue) => {
    const str = rawValue.trim();
    const amt = str === '' || !checkPositiveWholeNumber(str)
      ? 0
      : roundToPrecision(str, 2);
      // : roundToPrecision(str, 5);

    const pct = wireAmount > 0 ? (amt / wireAmount) * 100 : 0;
    setSecurityAllocations((current) => current.map((a) => (a.id === id
      ? { ...a, allocationAmount: amt, allocation: roundToPrecision(pct) }
      : a)));
  };

  const addNew = () => {
    if (securityAllocations.length >= 3) return;
    setSecurityAllocations([
      ...securityAllocations,
      {
        id: uuidv4(),
        securityType: -1,
        allocationAmount: 0,
        allocation: 0,
        numberOfUnits: 0,
        interestRate: roundToPrecision(0),
        sharePrice: roundToPrecision(1),
      },
    ]);
  };

  const removeOne = (id) => {
    if (securityAllocations.length <= 1) return;
    setSecurityAllocations((current) => current.filter((a) => a.id !== id));
  };

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      {securityAllocations.map((allocation) => {
        const sec = securityTypes.find((s) => s.id === allocation.securityType);
        // decide which extra fields to show
        const visibility = (() => {
          if (!sec || sec.name === undefinedSecurityTypeLabel) {
            return { showUnits: false, showRate: false, showPrice: false };
          }
          if (checkIfStockSecurity(sec.name) || checkIfTokenSecurity(sec.name)) {
            return { showUnits: true, showRate: false, showPrice: true };
          }
          if (checkIfNoInterestLoanSecurity(sec.name)) {
            return { showUnits: false, showRate: false, showPrice: false };
          }
          if (checkIfInterestLoanSecurity(sec.name)) {
            return { showUnits: false, showRate: true, showPrice: false };
          }
          return { showUnits: false, showRate: false, showPrice: false };
        })();

        return (
          <Box key={allocation.id} sx={{ p: 1, mb: 1, paddingLeft: 0 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                {isEditable ? (
                  <TextField
                    select
                    label="Security Type"
                    required
                    value={allocation.securityType}
                    onChange={(e) => updateField(allocation.id, 'securityType', Number(e.target.value))}
                    style={compactInputStyle}
                  >
                    <MenuItem value={-1}>Select security type…</MenuItem>
                    {securityTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <ReadOnlyTextField
                    label="Security Type"
                    value={sec ? sec.name : ''}
                    style={compactInputStyle}
                  />
                )}
              </Grid>

              <Grid item>
                {isEditable ? (
                  <CustomTextField
                    label="Allocation Amount"
                    type="number"
                    value={allocation.allocationAmount}
                    onChange={(e) => updateField(allocation.id, 'allocationAmount', e.target.value)}
                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                    onBlur={(e) => handleAllocationAmountChange(allocation.id, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    style={compactInputStyle}
                  />
                ) : (
                  <ReadOnlyTextField
                    label="Allocation Amount"
                    value={formatCurrencyAsUsd(allocation.allocationAmount)}
                    style={compactInputStyle}
                  />
                )}
              </Grid>

              {/* <Grid item>
                <ReadOnlyTextField
                  label="Allocation (%)"
                  value={Number(roundToPrecision(allocation.allocation, 5))}
                  style={compactInputStyle}
                />
              </Grid> */}

              {visibility.showUnits && (
                <Grid item>
                  {isEditable ? (
                    <CustomTextField
                      label="Number Of Units"
                      type="number"
                      value={allocation.numberOfUnits}
                      onChange={(e) => updateField(allocation.id, 'numberOfUnits', e.target.value)}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        const normalized = val === '' ? 0 : Math.round(Number(val));
                        updateField(allocation.id, 'numberOfUnits', normalized);
                      }}
                      style={compactInputStyle}
                    />
                  ) : (
                    <ReadOnlyTextField
                      label="Number of Units"
                      value={allocation.numberOfUnits}
                      style={compactInputStyle}
                    />
                  )}
                </Grid>
              )}

              {visibility.showRate && (
                <Grid item>
                  {isEditable ? (
                    <CustomTextField
                      label="Interest Rate"
                      type="number"
                      value={allocation.interestRate}
                      onChange={(e) => updateField(allocation.id, 'interestRate', e.target.value)}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        const normalized = val === '' ? 0 : roundToPrecision(val);
                        updateField(allocation.id, 'interestRate', normalized);
                      }}
                      style={compactInputStyle}
                    />
                  ) : (
                    <ReadOnlyTextField
                      label="Interest %"
                      value={allocation.interestRate}
                      style={compactInputStyle}
                    />
                  )}
                </Grid>
              )}

              {visibility.showPrice && (
                <Grid item>
                  {isEditable ? (
                    <CustomTextField
                      label="Share Price"
                      type="number"
                      value={allocation.sharePrice}
                      onChange={(e) => updateField(allocation.id, 'sharePrice', e.target.value)}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        const rounded = val === '' ? 0 : roundToPrecision(val, 8);
                        updateField(allocation.id, 'sharePrice', rounded);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      style={compactInputStyle}
                    />
                  ) : (
                    <ReadOnlyTextField
                      label="Share Price"
                      value={allocation.sharePrice}
                      style={compactInputStyle}
                    />
                  )}
                </Grid>
              )}

              {isEditable && securityAllocations.length > 1 && (
                <Grid item>
                  <Button
                    color="secondary"
                    fontSize="small"
                    variant="text"
                    style={{ marginLeft: '10px' }}
                    onClick={() => removeOne(allocation.id)}
                  >
                    Remove
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      })}

      {isEditable && securityAllocations.length < 3 && (
        <Button
          color="secondary"
          fontSize="small"
          variant="text"
          onClick={addNew}
        >
          Create Additional Security Type
        </Button>
      )}

      {/* {aggregatedInvestments && securityAllocations.length > 1 && (
        <Box sx={{ mt: 2, p: 1, borderRadius: 1 }}>
          <h5>Total Investment Amount Per Security Type</h5>
          {securityAllocations.map(alloc => {
            const total = aggregatedInvestments[alloc.securityType] || 0;
            const name =
              securityTypes.find(s => s.id === alloc.securityType)?.name ||
              'Unknown';
            return (
              <div key={alloc.id} style={{ margin: '5px 0' }}>
                <strong>{name}:</strong> {formatCurrencyAsUsd(total)}
              </div>
            );
          })}
        </Box>
      )} */}
    </Box>
  );
}
function checkWireDiscrepancyByType(
  funds,
  securityTypeAllocations,
  wireAmountRaw,
) {
  const wireAmount = getNumberValue(wireAmountRaw);
  const perType = {};

  securityTypeAllocations.forEach((alloc) => {
    const { securityType: typeId, sharePrice, numberOfUnits } = alloc;
    const typeName = securityTypes.find((s) => s.id === typeId)?.name || String(typeId);

    let subtotal = 0;
    if (
      checkIfStockSecurity(typeName)
      || checkIfTokenSecurity(typeName)
    ) {
      const price = getNumberValue(sharePrice);
      const units = getNumberValue(numberOfUnits);
      subtotal = price * units;
    } else {
      subtotal = funds
        .filter((f) => f.securityTypeId === typeId)
        .reduce((sum, f) => sum + getNumberValue(f.investmentAmount), 0);
    }

    perType[typeName] = roundToPrecision(subtotal, 2);
  });

  const expectedTotal = roundToPrecision(
    Object.values(perType).reduce((sum, v) => getNumberValue(sum) + getNumberValue(v), 0),
    2,
  );
  const difference = roundToPrecision(expectedTotal - wireAmount, 2);
  return {
    perType,
    expectedTotal,
    wireAmount,
    difference,
    isMatch: difference === 0,
  };
}
SecurityAllocationManager.propTypes = {
  securityAllocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      securityType: PropTypes.number.isRequired,
      allocation: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      numberOfUnits: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      interestRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      sharePrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }),
  ).isRequired,
  setSecurityAllocations: PropTypes.func.isRequired,
  securityTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isEditable: PropTypes.bool.isRequired,
  aggregatedInvestments: PropTypes.object.isRequired,
  wireAmount: PropTypes.string.isRequired,
};

function PostWireSignOffNetsuiteSubmission() {
  const { id: dealId } = useParams();
  const breakdownDGRef = useGridApiRef();
  const [loadingDeal, setDealsDetailLoading] = useState(true);
  const [fundAllocations, setFundAllocations] = useState(null);
  const [originalFunds, setOriginalFunds] = useState(null);
  const [originalFundsBeforeSecurityTypeProration, setOriginalFundsBeforeSecurityTypeProration] = useState(null);
  const [displayedFunds, setDisplayedFunds] = useState(null);
  const totalInvestmentsId = uuidv4();
  const [avInvestmentTotals, setAvInvestmentTotals] = useState([
    {
      id: totalInvestmentsId,
      avInvestmentTotal: 0,
      avInvestmentATotal: 0,
      avInvestmentQTotal: 0,
    },
  ]);
  const [wireTransferDate, setWireTransferDate] = useState(null);
  const [roundNumber, setRoundNumber] = useState(1);
  const [fundConfigError, setFundConfigError] = useState(null);
  const [closingDocuments, setClosingDocuments] = useState([]);
  const [isPushToNSConfirmationModalOpen, setPushToNSConfirmationModalOpen] = useState(false);
  const [wireDateError, setWireDateError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPushingToNS, setIsPushingToNS] = useState(false);
  const [wireAmountError, setWireAmountError] = useState('');
  const [isWireAmountErrorDiscrepencyOverLimit, setIsWireAmountErrorDiscrepencyOverLimit] = useState(false);
  const [sharePriceXNumberOfUnitsError, setSharePriceXNumberOfUnitsError] = useState('');
  const [isSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit, setIsSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit] = useState(false);
  const user = new User();
  const [userRole] = useState({ isFinance: user.isFinance() });
  const history = useHistory();

  const [securityAllocations, setSecurityAllocations] = useState([
    {
      id: uuidv4(),
      securityType: -1,
      allocation: 100,
      numberOfUnits: 0,
      interestRate: roundToPrecision(0),
      sharePrice: roundToPrecision(1),
      allocationAmount: 0,
    },
  ]);

  // Validate the security allocations.
  const validateSecurityAllocations = () => {
    if (securityAllocations.length < 1 || securityAllocations.length > 3) {
      return false;
    }
    if (securityAllocations.some((item) => item.allocation <= 0)) {
      return false;
    }
    const selectedTypes = securityAllocations.map((item) => item.securityType);
    if (selectedTypes.includes(-1)) {
      return false;
    }
    if (new Set(selectedTypes).size !== securityAllocations.length) {
      return false;
    }
    const totalAllocation = securityAllocations.reduce(
      (sum, item) => sum + Number(item.allocation),
      0,
    );

    // Allow within 3 cents below 100%, but never above 100%
    if (totalAllocation > 100) {
      return false;
    }
    if (totalAllocation < (100 - maxAllowableWireAmountVsTotalAllocationAcrossSecuritiesDiscrepancy)) {
      return false;
    }
    return true;
  };

  const isSecurityAllocationsValid = validateSecurityAllocations();

  const calculateTotals = (funds) => {
    const investmentTotals = {
      id: totalInvestmentsId,
      avInvestmentTotal: 0,
      avInvestmentATotal: 0,
      avInvestmentQTotal: 0,
    };
    funds.forEach((fundAllocation) => {
      const aAllocation = getNumberValue(fundAllocation.aAllocation);
      const qAllocation = getNumberValue(fundAllocation.qAllocation);
      investmentTotals.avInvestmentTotal += (aAllocation + qAllocation);
      investmentTotals.avInvestmentATotal += aAllocation;
      investmentTotals.avInvestmentQTotal += qAllocation;
    });
    investmentTotals.avInvestmentTotal = formatCurrencyAsUsd(
      investmentTotals.avInvestmentTotal,
    );
    investmentTotals.avInvestmentATotal = formatCurrencyAsUsd(
      investmentTotals.avInvestmentATotal,
    );
    investmentTotals.avInvestmentQTotal = formatCurrencyAsUsd(
      investmentTotals.avInvestmentQTotal,
    );
    return investmentTotals;
  };

  const setTotals = (funds) => {
    const investmentTotals = calculateTotals(funds);
    setAvInvestmentTotals([investmentTotals]);
    return investmentTotals;
  };

  const autoCalculateNumberOfUnitsForLoanTypes = (securityTypeId) => {
    const filteredSecurityType = securityTypes.find(
      (st) => st.id === securityTypeId,
    );
    if (!filteredSecurityType) {
      return 0;
    }
    const securityTypeName = filteredSecurityType.name;
    if (
      checkIfInterestLoanSecurity(securityTypeName)
      || checkIfNoInterestLoanSecurity(securityTypeName)
    ) {
      if (fundAllocations && fundAllocations[0] && breakdownDGRef && breakdownDGRef.current) {
        const totals = calculateTotals(
          exportGridValues(breakdownDGRef.current, Object.keys(fundAllocations[0])),
        );
        let avInvestmentTotal = getNumberValue(totals.avInvestmentTotal);
        const securityAllocation = securityAllocations.find(
          (allocation) => allocation.securityType === securityTypeId,
        );
        if (securityAllocation) {
          avInvestmentTotal *= (securityAllocation.allocation / 100);
        }
        return Math.ceil(avInvestmentTotal);
      }
    }
    return 0;
  };

  const isMultiSecurityDeal = securityAllocations.length > 1;

  // const sourceFunds = isMultiSecurityDeal ? originalFunds : displayedFunds;
  useEffect(() => {
    if (originalFunds && securityAllocations) {
      const newFunds = createProratedFunds(originalFunds, securityAllocations);
      const formattedFundAllocations = prepareFundAllocationsForGrid(newFunds);
      setFundAllocations(formattedFundAllocations);
      const clonedFunds = JSON.parse(JSON.stringify(formattedFundAllocations));
      setDisplayedFunds(clonedFunds);
      // add up the investment amounts for security type id 1
      const total = clonedFunds.reduce((acc, fund) => {
        if (fund.securityTypeId === 1) {
          return acc + getNumberValue(fund.investmentAmount);
        }
        return acc;
      }, 0);
    }
  }, [originalFunds, securityAllocations]);

  useEffect(() => {
    if (displayedFunds) {
      if (
        breakdownDGRef
        && breakdownDGRef.current
        && breakdownDGRef.current.getAllRowIds
      ) {
        setTotals(
          exportGridValues(
            breakdownDGRef.current,
            Object.keys(displayedFunds[0]),
          ),
        );
      }
    }
  }, [displayedFunds]);

  // Compute total investment amount per security type from displayed funds.
  const totalsPerSecurity = displayedFunds
    ? displayedFunds.reduce((acc, fund) => {
      if (fund.securityTypeId && fund.securityTypeId >= 0) {
        acc[fund.securityTypeId] = (acc[fund.securityTypeId] || 0) + getNumberValue(fund.investmentAmount);
      }
      return acc;
    }, {})
    : {};

  const deal = useDealDetail(dealId, (dealData) => {
    const oFunds = dealData.originalFundsBeforeSecurityTypeProration || dealData.funds;
    if (!dealData.originalFundsBeforeSecurityTypeProration) {
      setOriginalFundsBeforeSecurityTypeProration([
        ...dealData.funds,
      ]);
    }
    let formattedFundAllocations = prepareFundAllocationsForGrid(dealData.funds);
    const aggregatedFunds = createAggregatedFunds(formattedFundAllocations);
    formattedFundAllocations = Object.values(aggregatedFunds);
    setFundAllocations(formattedFundAllocations);
    const clonedFunds = JSON.parse(JSON.stringify(formattedFundAllocations));
    setOriginalFunds(oFunds);
    setDisplayedFunds(clonedFunds);
    setDealsDetailLoading(false);
    setClosingDocuments(dealData.closingDocuments || []);
    setRoundNumber(dealData.roundNumber);
    let savedSecurityAllocations = [];
    if (dealData.securityAllocations) {
      savedSecurityAllocations = [...dealData.securityAllocations];
    } else if (dealData.securityType) {
      const existingSecurityType = savedSecurityAllocations.find(
        (allocation) => allocation.securityType === dealData.securityType,
      );
      if (!existingSecurityType) {
        savedSecurityAllocations.push({
          id: uuidv4(),
          securityType: dealData.securityType,
          allocation: 100,
          numberOfUnits: dealData.numberOfUnits,
          interestRate: dealData.interestRate,
          sharePrice: dealData.sharePrice,
        });
      }
    } else {
      savedSecurityAllocations.push({
        id: uuidv4(),
        securityType: -1,
        allocation: 100,
        numberOfUnits: 0,
        interestRate: roundToPrecision(0),
        sharePrice: roundToPrecision(1),
      });
    }
    const wireAmt = getNumberValue(dealData.wireAmount) || 0;

    savedSecurityAllocations = savedSecurityAllocations.map((a) => ({
      ...a,
      // compute dollars = wireAmt * (percent / 100), rounded to cents.
      allocationAmount: roundToPrecision((wireAmt * (a.allocation / 100)), 2),
    }));
    setSecurityAllocations(savedSecurityAllocations);
  });
  const isNonSecurityTypeProratedFunds = !isMultiSecurityDeal
    && getNumberValue(securityAllocations[0]?.allocationAmount) === getNumberValue(deal?.wireAmount);
  // useEffect to update totals and wire amount error when dependencies change
  useEffect(() => {
    if (breakdownDGRef) {
      if (fundAllocations && fundAllocations[0]) {
        setTotals(
          exportGridValues(breakdownDGRef.current, Object.keys(fundAllocations[0])),
        );
      }
      if (!loadingDeal) {
        if (deal.wireTransferDate) {
          setWireTransferDate(deal.wireTransferDate);
        }
        if (deal.processStage === 2) {
          history.push('/');
        }
        setFundConfigError(createFundConfigurationError(deal.funds));
        return breakdownDGRef.current.subscribeEvent(
          'cellModeChange',
          (params, event) => {
            event.defaultMuiPrevented = true;
          },
          { isFirst: true },
        );
      }
    }
    return () => { };
  }, [loadingDeal]);

  clearInterval(window.postWireAmountIntervalCheck);
  window.postWireAmountIntervalCheck = setInterval(() => {
    if (deal && avInvestmentTotals && displayedFunds) {
      const avInvestmentTotal = roundToPrecision(getNumberValue(avInvestmentTotals[0].avInvestmentTotal), 2);
      const dealWireAmount = roundToPrecision(getNumberValue(deal.wireAmount), 2);
      const wireAmountDiffWithAvInvestmentTotal = Math.abs(getNumberValue(avInvestmentTotal) - getNumberValue(dealWireAmount));
      const wireAmountErrorMessage = `
            The total investment amount of ${formatCurrencyAsUsd(avInvestmentTotal)} 
            does not match the wire amount of ${formatCurrencyAsUsd(dealWireAmount)} specified in the deal. 
            Difference is ${formatCurrencyAsUsd(wireAmountDiffWithAvInvestmentTotal)}.`;
      if (wireAmountDiffWithAvInvestmentTotal > maxAllowableWireAmountVsTotalInvestmentAmountDiscrepancy) {
        setWireAmountError(wireAmountErrorMessage);
        setIsWireAmountErrorDiscrepencyOverLimit(true);
      } else if (wireAmountDiffWithAvInvestmentTotal !== 0
        && wireAmountDiffWithAvInvestmentTotal <= maxAllowableWireAmountVsTotalInvestmentAmountDiscrepancy) {
        setWireAmountError(wireAmountErrorMessage);
        setIsWireAmountErrorDiscrepencyOverLimit(false);
      } else if (wireAmountError) {
        setWireAmountError('');
        setIsWireAmountErrorDiscrepencyOverLimit(false);
      }
      const sharePriceCheck = checkWireDiscrepancyByType(displayedFunds, securityAllocations, deal.wireAmount);
      const spxnouError = `
            Share Price x Number of Units is ${formatCurrencyAsUsd(sharePriceCheck.expectedTotal)} but Wire Amount is 
            ${formatCurrencyAsUsd(dealWireAmount)}. Difference is ${formatCurrencyAsUsd(sharePriceCheck.difference)}.`;
      const diff = Math.abs(getNumberValue(sharePriceCheck.difference));
      if (diff > maxAllowableSharePriceXNumberUnitsDiscrepancy) {
        setSharePriceXNumberOfUnitsError(spxnouError);
        setIsSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit(true);
      } else if (diff !== 0 && diff <= maxAllowableSharePriceXNumberUnitsDiscrepancy) {
        setSharePriceXNumberOfUnitsError(spxnouError);
        setIsSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit(false);
      } else {
        setSharePriceXNumberOfUnitsError('');
        setIsSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit(false);
      }
    }
  }, 600);

  const handleCellClick = useCallback((params) => {
    if (params.isEditable) {
      breakdownDGRef.current.setCellMode(params.id, params.field, 'edit');
    }
  }, [breakdownDGRef]);

  const dealsTableDef = createDealsTableDefinition(userRole, isMultiSecurityDeal, isNonSecurityTypeProratedFunds);
  if (!userRole.isFinance) {
    dealsTableDef.forEach((column) => {
      column.editable = false;
    });
  }

  const preparePostPayload = (pendingInvestment) => {
    pendingInvestment.funds = displayedFunds;
    const gridValues = exportGridValues(
      breakdownDGRef.current,
      Object.keys(fundAllocations[0]),
    );

    const payload = assignLocalEditValues(
      pendingInvestment,
      gridValues,
      { wireTransferDate, roundNumber },
    );
    return payload;
  };

  const completePayload = (pendingInvestment) => {
    const payload = preparePostPayload(pendingInvestment);
    payload.originalFundsBeforeSecurityTypeProration = originalFundsBeforeSecurityTypeProration;
    const updatedAllocations = securityAllocations.map((allocation) => {
      const filteredSecurity = securityTypes.find(
        (s) => s.id === allocation.securityType,
      );
      if (
        filteredSecurity
        && (checkIfInterestLoanSecurity(filteredSecurity.name)
          || checkIfNoInterestLoanSecurity(filteredSecurity.name))
      ) {
        return {
          ...allocation,
          numberOfUnits:
            autoCalculateNumberOfUnitsForLoanTypes(allocation.securityType)
            || allocation.numberOfUnits,
        };
      }
      return allocation;
    });
    payload.securityAllocations = updatedAllocations;
    return payload;
  };

  const overwriteAndSaveEdits = async (pendingInvestment) => {
    setIsSubmitting(true);
    await updatePendingInvestment(generateRequest(completePayload(pendingInvestment)));
    setIsSubmitting(false);
  };

  const submitToNetsuite = async (pendingInvestment) => {
    setIsSubmitting(true);
    await submitPendingInvestment(generateRequest(completePayload(pendingInvestment)));
    setIsSubmitting(false);
  };

  const isFollowOnRound = deal && deal.roundId && deal.roundNumber;
  const roundNumberLabel = isFollowOnRound ? 'Round Number (Extension)' : 'Round Number';
  return (
    <>
      <Helmet>
        <title>Finance Wire Sign Off To NetSuite</title>
      </Helmet>
      <Container className="dashboard" style={{ maxWidth: '100%' }}>
        <Card>
          <CardBody>
            <Row>
              <Col>
                {deal && <h2>{deal.companyName}</h2>}
                {!deal && HorizRecLoader(1, '250px', 30)}
              </Col>
            </Row>
            <Row>
              <Col style={{ marginTop: '30px' }}>
                {deal && (
                  <DatePicker
                    label="Wire Date (Investment Date)"
                    value={wireTransferDate}
                    renderInput={(params) => (
                      <CustomTextField
                        required
                        style={{ minWidth: '285px' }}
                        {...params}
                        customreadonly={!userRole.isFinance}
                      />
                    )}
                    InputProps={{ readOnly: true }}
                    onChange={(date) => { setWireTransferDate(date); }}
                    onError={(error) => { setWireDateError(error); }}
                  />
                )}
                {deal && (
                  <Box
                    sx={{
                      p: 1,
                      m: 2,
                      marginLeft: '0px',
                      paddingLeft: '0px',
                    }}
                  >
                    <CustomTextField
                      id="roundNumberInput"
                      label={roundNumberLabel}
                      type="number"
                      required
                      defaultValue={
                        !checkIfNullOrUndefined(deal.roundNumber)
                          ? deal.roundNumber
                          : roundNumber
                      }
                      style={{ width: '124px' }}
                      InputLabelProps={{ shrink: true }}
                      onBlur={(event) => {
                        if (!checkNaturalNumber(event.target.value)) {
                          event.target.value = 1;
                        }
                        const newValue = Math.round(event.target.value);
                        event.target.value = newValue;
                        setRoundNumber(newValue);
                      }}
                      customreadonly={!userRole.isFinance || isFollowOnRound}
                    />
                  </Box>
                )}
                {deal && (
                  <>
                    <SecurityAllocationManager
                      securityAllocations={securityAllocations}
                      setSecurityAllocations={setSecurityAllocations}
                      securityTypes={securityTypes}
                      isEditable={userRole.isFinance}
                      aggregatedInvestments={totalsPerSecurity}
                      wireAmount={roundToPrecision(getNumberValue(deal.wireAmount), 2)}
                    />
                    {userRole.isFinance && sharePriceXNumberOfUnitsError && !isSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit && (
                      <p style={{ marginLeft: '0px' }}>
                        <WarnIcon />   {sharePriceXNumberOfUnitsError}
                      </p>
                    )}
                    {userRole.isFinance && sharePriceXNumberOfUnitsError && isSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit && (
                      <p className="error red" style={{ marginLeft: '0px' }}>
                        <WarnIcon /> {sharePriceXNumberOfUnitsError}
                      </p>
                    )}
                    {userRole.isFinance && wireAmountError && !isWireAmountErrorDiscrepencyOverLimit && (
                      <p style={{ marginLeft: '0px' }}>
                        <WarnIcon /> {wireAmountError}
                      </p>
                    )}
                    {userRole.isFinance && wireAmountError && isWireAmountErrorDiscrepencyOverLimit && (
                    <p className="error red" style={{ marginLeft: '0px' }}>
                      {wireAmountError}
                    </p>
                    )}
                    {!isSecurityAllocationsValid && (
                      <div className="error red">
                        Please ensure:
                        <ul>
                          <li>Each security type has an allocation greater than 0.</li>
                          <li>Each security type is determined and unique.</li>
                          <li>Total allocation is between 99.97% and 100% of the wire amount.</li>
                        </ul>
                      </div>
                    )}

                  </>
                )}
                {!deal && (
                  <>
                    {HorizRecLoader(1, 285)}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        p: 0,
                        m: 0,
                        borderRadius: 1,
                      }}
                    >
                      {HorizRecLoader(4)}
                    </Box>
                  </>
                )}
                {(userRole.isFinance && fundConfigError) && (
                  <h4 className="error red">{fundConfigError}</h4>
                )}
                <div style={{ marginTop: '30px' }}>
                  <AvDataGrid
                    rows={displayedFunds || []}
                    columns={dealsTableDef}
                    apiRef={breakdownDGRef}
                    loading={loadingDeal}
                    onCellEditStop={() => {
                      if (isNonSecurityTypeProratedFunds) {
                        setOriginalFunds(
                          exportGridValues(
                            breakdownDGRef.current,
                            Object.keys(fundAllocations[0]),
                          ),
                        );
                      }
                      setTotals(
                        exportGridValues(
                          breakdownDGRef.current,
                          Object.keys(fundAllocations[0]),
                        ),
                      );
                    }}
                    onCellClick={(params) => { handleCellClick(params); }}
                    initialState={{
                      sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                    }}
                  />
                </div>
                <div style={{ marginTop: '20px' }}>
                  <AvDataGrid
                    pageSize={1}
                    rows={avInvestmentTotals}
                    columns={createDealTotalsTableDefinition()}
                    loading={loadingDeal}
                    hideFooter
                    density="compact"
                    disableColumnMenu
                    disableColumnFilter
                    disableColumnSelector
                    sx={{ fontSize: '17px' }}
                  />
                </div>
              </Col>
            </Row>
            {deal && (
              <Stack>
                <Row className="w-100">
                  <Col>
                    <DocumentViewer
                      id={Number(dealId)}
                      documents={closingDocuments}
                      label="Closing Documents"
                    />
                  </Col>
                </Row>
              </Stack>
            )}
            {deal && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                spacing={2}
                mt={2}
              >

                {userRole.isFinance && (
                  <StyledActionButton
                    onClick={async () => {
                      setIsSaving(true);
                      await overwriteAndSaveEdits(deal);
                      setIsSaving(false);
                    }}
                    disabled={isSubmitting || !isSecurityAllocationsValid}
                    loading={isSaving}
                  >
                    Save As Draft
                  </StyledActionButton>
                )}
                {userRole.isFinance && (
                  <StyledActionButton
                    onClick={async () => {
                      setIsPushingToNS(true);
                      setPushToNSConfirmationModalOpen(true);
                    }}
                    variant="contained"
                    disabled={
                      !!fundConfigError
                      || !wireTransferDate
                      || wireDateError
                      || isSubmitting
                      || !isSecurityAllocationsValid
                      || (!!wireAmountError && isWireAmountErrorDiscrepencyOverLimit)
                      || (!!sharePriceXNumberOfUnitsError && isSharePriceXNumberOfUnitsErrorDiscrepencyOverLimit)
                    }
                    loading={isPushingToNS}
                  >
                    Push to Netsuite
                  </StyledActionButton>
                )}
              </Stack>
            )}
            {deal && !userRole.isFinance && (
              <h4 style={{ textAlign: 'center' }}>
                This page is editable by Finance users only
              </h4>
            )}
            {(!deal && userRole.isFinance) && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                spacing={2}
                mt={2}
              >
                {HorizRecLoader(2)}
              </Stack>
            )}
          </CardBody>
        </Card>
      </Container>
      <ConfirmationModal
        isOpen={isPushToNSConfirmationModalOpen}
        headerText="Are you sure you want to push changes to NetSuite?"
        bodyText="Please confirm all values are correct as this cannot be easily undone."
        onConfirm={async () => {
          deal.processStage = 2;
          await submitToNetsuite(deal);
          history.push('/');
        }}
        onCancel={() => {
          setPushToNSConfirmationModalOpen(false);
          setIsPushingToNS(false);
        }}
      />
    </>
  );
}

export default PostWireSignOffNetsuiteSubmission;
